import { useState } from 'react';
import { Container, Grid, MenuItem, Modal, Typography, makeStyles } from '@material-ui/core'
import { colours, theme } from '../constants/theme'
import { ButtonWithLoadingIndicator, CancelButton } from "../components-atoms";
import { buttonHoverAndActive } from '../utils/styles/buttonStyles';
import {
  markAsCanceledRequest,
  markAsDeliveredRequest,
  markAsFailedRequest
} from '../redux/actions/collectOrders';
import ThreeDotMenu from "../components-molecules/ThreeDotMenu";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import CancelIcon from '@material-ui/icons/Cancel';

const useStyles = makeStyles(() => ({
  root: {
    overflow: "visible",
    paddingLeft: 0,
    paddingRight: 0,
    "@media (min-width: 900px)": {
      maxWidth: "840px",
    },
  },
  actionButtonsContainer: {
    display: "flex",
    marginTop: "16px",
    alignItems: "flex-end",
    justifyContent: "flex-end",
  },
  imageText: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  infoText: {
    fontWeight: "normal",
    color: colours.highEmphasis54,
  },
  modal: { alignItems: "center", justifyContent: "center" },
  modalContainer: {
    backgroundColor: "white",
    maxWidth: "480px",
    boxShadow:
      "0px 6px 36px rgba(0, 0, 0, 0.15), 0px 2.21728px 14.2392px rgba(0, 0, 0, 0.111884), 0px 0.93179px 6.99399px rgba(0, 0, 0, 0.0997219), 0px 0.37078px 3.36556px rgba(0, 0, 0, 0.0894965), 0px 0.120827px 1.42249px rgba(0, 0, 0, 0.0753326), 0px 0.0225271px 0.422798px rgba(0, 0, 0, 0.0508791)",
    borderRadius: "20px",
    padding: "24px",
    margin: "0",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },

  titleSection: {
    marginBottom: "8px",
  },
  bodySection: {
    margin: "24px 0 36px 0",
  },
  rejectButton: {
    borderRadius: "8px",
    padding: "6px 16px",
    ...buttonHoverAndActive
  },
  mt8: {
    marginTop: 8,
  },
  menuItem: {
    margin: '8px 4px',
    borderRadius: 8
  },
  itemText: {
    paddingLeft: 16
  }
}));

enum ModalType {
  MarkAsDelivered = "MarkAsDelivered",
  MarkAsCanceled = "MarkAsCanceled",
  MarkAsFailed = "MarkAsFailed",
}

export default function CollectionOrderHeaderActions({ data }: { data: any }) {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState<ModalType>();

  const onClose = () => {
    setLoading(false);
    setModalOpen(undefined);
  }

  const onAccept = () => {
    setLoading(true);
    let requestFunc;

    switch (modalOpen) {
      case ModalType.MarkAsDelivered:
        requestFunc = markAsDeliveredRequest;
        break;
      case ModalType.MarkAsCanceled:
        requestFunc = markAsCanceledRequest;
        break;
      case ModalType.MarkAsFailed:
        requestFunc = markAsFailedRequest;
        break;
    }

    requestFunc(
      { orderId: data.Id },
      () => {
        window.location.reload();
      },
      (err) => {
        setLoading(false);
      }
    );
  }

  const getActionText = () => {
    switch (modalOpen) {
      case ModalType.MarkAsDelivered:
        return 'delivered';
      case ModalType.MarkAsCanceled:
        return 'canceled';
      case ModalType.MarkAsFailed:
        return 'failed';
    }
  }

  if (data.Delivered && data.Cancelled && data.Failed) return null;

  return (
    <div>
      <ThreeDotMenu>
        {!data.Delivered &&
          <MenuItem
            className={classes.menuItem}
            style={{ color: theme.palette.success.main }}
            onClick={() => setModalOpen(ModalType.MarkAsDelivered)}>
            <CheckCircleIcon />
            <Typography variant="body2" component="p" className={classes.itemText}>
              Mark as delivered
            </Typography>
          </MenuItem>
        }
        {!data.Cancelled &&
          <MenuItem
            className={classes.menuItem}
            style={{ color: theme.palette.warning.main }}
            onClick={() => setModalOpen(ModalType.MarkAsCanceled)}>
            <CancelIcon />
            <Typography variant="body2" component="p" className={classes.itemText}>
              Mark as canceled
            </Typography>
          </MenuItem>
        }
        {!data.Failed &&
          <MenuItem
            className={classes.menuItem}
            style={{ color: theme.palette.error.main }}
            onClick={() => setModalOpen(ModalType.MarkAsFailed)}>
            <ErrorIcon />
            <Typography variant="body2" component="p" className={classes.itemText}>
              Mark as failed
            </Typography>
          </MenuItem>
        }
      </ThreeDotMenu>

      <Modal
        open={modalOpen !== undefined}
        onClose={onClose}
        className={classes.modal}>
        <Container className={classes.modalContainer}>
          <Grid container spacing={1}></Grid>
          <Grid item className={classes.titleSection}>
            <Typography variant="h6" component="h6" color="textPrimary">
              Update order status
            </Typography>
          </Grid>
          <Grid item className={classes.bodySection}>
            <Typography
              variant="body2"
              component="p"
              className={classes.mt8}
              color="textPrimary"
            >
              Are you sure you want to mark <strong>{data.ReferenceNumber}</strong> as {getActionText()}?
            </Typography>
          </Grid>
          <Grid item container xs={12} justifyContent="flex-end">
            <CancelButton onClick={() => setModalOpen(undefined)} />
            <ButtonWithLoadingIndicator
              loading={loading}
              onClick={onAccept}
              className={classes.rejectButton}
              title="Yes, proceed"
            />
          </Grid>
        </Container>
      </Modal>
    </div>
  )
}
